import type { ReactNode } from "react";
import type { FieldValues, SubmitErrorHandler, UseFormReturn } from "react-hook-form";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Form } from "@/shared/components/ui/form";
import { cn } from "@/shared/lib/cn";

interface FormDialogProps<TValues extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /// RHF instance owned by the caller. Caller should construct via
  /// `useForm({ resolver: zodResolver(schema), defaultValues })`.
  form: UseFormReturn<TValues>;
  /// Submit handler invoked with validated values.
  onSubmit: (values: TValues) => void | Promise<void>;
  /// Optional invalid-submit handler so callers can surface hidden validation
  /// failures without re-implementing the form shell.
  onInvalid?: SubmitErrorHandler<TValues>;
  title: string;
  description?: string;
  /// Form body children. Use `<FormField>` from `@/shared/components/ui/form`
  /// and the wrapped <TextField>/<SelectField>/etc. from
  /// `@/shared/components/form/fields/*` so labels, errors, and a11y wire up.
  children: ReactNode;
  /// Visible label for the primary action. Pair with `submitLoadingLabel` to
  /// surface the pending-state copy.
  submitLabel: string;
  /// Optional pending-state label (e.g. "Saving…"). Defaults to `submitLabel`.
  submitLoadingLabel?: string;
  /// Cancel-button copy. Defaults to "Cancel".
  cancelLabel?: string;
  /// Variant for the primary submit button.
  submitVariant?: "default" | "destructive";
  /// Extra disabled gate beyond `formState.isSubmitting` + Zod validity.
  /// Use for cross-field client guards that complement Zod.
  submitDisabled?: boolean;
  /// Server-level error to display above the footer (e.g. 500 outcomes).
  /// Inline field errors go through the <Field> components.
  serverError?: string | null;
  /// Standard width preset. Defaults to `"lg"` (512px).
  /// - `"md"` (448px) - confirms, short single-column forms (invite, rename).
  /// - `"lg"` (512px) - default; most create/edit dialogs.
  /// - `"xl"` (576px) - forms with two-column layout or chip rows.
  /// - `"2xl"` (672px) - rich forms with multi-pickers + extras (kudos).
  size?: "md" | "lg" | "xl" | "2xl";
  /// Escape hatch for one-off widths. Prefer `size` whenever possible.
  contentClassName?: string;
  /// When false, the dialog closes only via the X or Cancel button, not on an
  /// outside click or Escape. Use for forms where a misclick would discard input.
  dismissible?: boolean;
  /// A second committing action, rendered between Cancel and Submit. Only for
  /// forms whose primary action is not the only way to keep the work: an
  /// expense claim can be saved as a draft or submitted, and the two are one
  /// form, not two. Pass a `type="button"` control that runs its own
  /// `form.handleSubmit`, so it validates the same fields the footer does.
  secondaryAction?: ReactNode;
}

const SIZE_CLASS: Record<NonNullable<FormDialogProps<FieldValues>["size"]>, string> = {
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
};

/// Standard form dialog shell for entity create/edit flows (rule F1).
///
/// - `sm:max-w-lg` modal, scrollable body capped at the viewport so long
///   forms stay reachable on small screens.
/// - Footer: outline Cancel (never disabled) + primary Submit that flips
///   to `submitLoadingLabel` while `formState.isSubmitting`.
/// - `key`-remounts the form on each open so RHF state resets without
///   the manual `reset()`-in-effect pattern (S9).
/// - `serverError` lives inside the form, above the footer, at `text-sm`.
export function FormDialog<TValues extends FieldValues>({
  open,
  onOpenChange,
  form,
  onSubmit,
  onInvalid,
  title,
  description,
  children,
  submitLabel,
  submitLoadingLabel,
  cancelLabel = "Cancel",
  submitVariant = "default",
  submitDisabled = false,
  serverError,
  size = "lg",
  contentClassName,
  dismissible = true,
  secondaryAction,
}: FormDialogProps<TValues>): ReactNode {
  const { isSubmitting } = form.formState;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        key={open ? "open" : "closed"}
        className={cn(SIZE_CLASS[size], contentClassName)}
        dismissible={dismissible}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            className="flex flex-col gap-4"
            noValidate
          >
            <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">{children}</div>
            {serverError ? (
              <p role="alert" className="text-sm text-destructive-text">
                {serverError}
              </p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {cancelLabel}
              </Button>
              {secondaryAction}
              <Button
                type="submit"
                variant={submitVariant}
                disabled={isSubmitting || submitDisabled}
              >
                {isSubmitting ? (submitLoadingLabel ?? submitLabel) : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
